(ns manage.content-variant-ocp.db.column-classification
  (:require [clojure.spec.alpha :as spec]
            [common.form.core :as form]
            [common.loader.db :as loader.db]
            [common.ocp.db]
            [common.util :as util]
            [manage.adset.util :as adset-util]
            [manage.content-variant-ocp.db.core :refer [->external-classification entity-name field-classes-flow ui-messages]]
            [manage.resource.feed-imports :as feed-imports]
            [mate.core :as mc]
            [mate.re-frame :as mr]
            [phrase.alpha :as p]
            [re-frame.core :as rf]))

(defn get-adset-channel-type
  [adset]
  (let [target-type (or (:targetType adset) "dsp")] ; falling back to dsp if targetType is nil
    (adset-util/target-type->channel target-type)))

(def classification-value->visibility
  {"identifier-key" #{:all}
   "content-key" #{:all}
   "targeting-key" #{:all}
   "click-through-key" #{:all}
   "metadata-key" #{:youtube}
   "third-party-impression-tracker-key" #{:display :video :ctv}
   "third-party-click-tracker-key" #{:display :video :ctv}
   "template-dimensions-key" #{:display}
   "clear-cast-id-key" #{:ctv}
   "optimizing-key" #{:display :video :ctv}
   "youtube-description-key" #{:youtube}
   "meta-headline-key" #{:meta}
   "meta-primary-text-key" #{:meta}
   "meta-description-key" #{:meta}
   "meta-cta-key" #{:meta}
   "meta-cta-caption-key" #{:meta}
   "tiktok-ad-name-key" #{:tiktok}
   "tiktok-ad-text-key" #{:tiktok}
   "tiktok-cta-key" #{:tiktok}})

(defn get-classification-options [content-optimizing-enabled?]
  (cond-> [{:label "Identifier"
            :value "identifier-key"}
           {:label "Content"
            :value "content-key"}
           {:label "Targeting data"
            :value "targeting-key"}
           {:label "Click through"
            :value "click-through-key"}
           {:label "Meta data"
            :value "metadata-key"}
           {:label "Impression tracker"
            :value "third-party-impression-tracker-key"}
           {:label "Click tracker"
            :value "third-party-click-tracker-key"}
           {:label "Dimensions"
            :value "template-dimensions-key"}]
    (= :youtube @(rf/subscribe [:resource.adset/current-target-type]))
    (conj {:label "YouTube Description"
           :value "youtube-description-key"})
    (= :finecast @(rf/subscribe [:resource.adset/current-target-type]))
    (conj {:label "ClearCast Id"
           :value "clear-cast-id-key"})
    content-optimizing-enabled?
    (conj {:label "Optimizing key"
           :value "optimizing-key"})
    (= "meta" @(rf/subscribe [:resource.adset/current-channel]))
    (conj {:label "Meta Headline"
           :value "meta-headline-key"}
          {:label "Meta Primary Text"
           :value "meta-primary-text-key"}
          {:label "Meta Description"
           :value "meta-description-key"}
          {:label "Meta CTA"
           :value "meta-cta-key"}
          {:label "Meta Display Link"
           :value "meta-cta-caption-key"})
    (= "tiktok" @(rf/subscribe [:resource.adset/current-channel]))
    (conj {:label "TikTok Ad Name"
           :value "tiktok-ad-name-key"}
          {:label "TikTok Ad Text"
           :value "tiktok-ad-text-key"}
          {:label "TikTok CTA"
           :value "tiktok-cta-key"})))

(defn get-filtered-classification-options [content-optimizing-enabled?]
  (let [adset @(rf/subscribe [:resource.adset/one])
        adset-channel-type (get-adset-channel-type (:value adset))
        opts (get-classification-options content-optimizing-enabled?)]
    (filter #(let [mapping (classification-value->visibility (:value %))]
               (or (contains? mapping :all)
                   (contains? mapping adset-channel-type)))
            opts)))

(defmulti -valid-key-frequency? (fn [key _key-frequency] key))

(defmethod -valid-key-frequency? "identifier-key"
  [_key key-frequency]
  (= 1 key-frequency))

(defmethod -valid-key-frequency? "click-through-key"
  [_key key-frequency]
  (let [adset-channel @(rf/subscribe [:resource.adset/current-channel])]
    (and (>= key-frequency 1)
         (mc/implies (not= adset-channel "display") (= key-frequency 1)))))

(defmethod -valid-key-frequency? "youtube-description-key"
  [_key key-frequency]
  (let [adset-target-type @(rf/subscribe [:resource.adset/current-target-type])]
    (mc/implies (= adset-target-type :youtube) (= key-frequency 1))))

(defmethod -valid-key-frequency? "third-party-click-tracker-key"
  [_key key-frequency]
  (or (nil? key-frequency)
      (= 1 key-frequency)))

(defmethod -valid-key-frequency? "clear-cast-id-key"
  [_key key-frequency]
  (or (nil? key-frequency)
      (= 1 key-frequency)))

(defmethod -valid-key-frequency? "template-dimensions-key"
  [_key key-frequency]
  (or (nil? key-frequency)
      (= 1 key-frequency)))

(defmethod -valid-key-frequency? "optimizing-key"
  [_key key-frequency]
  (or (nil? key-frequency)
      (< key-frequency 5)))

(defn- valid-key-frequency? [key classifications]
  (let [{key-frequency key} (frequencies (vals classifications))]
    (-valid-key-frequency? key key-frequency)))

(spec/def ::classification-options
  (spec/nilable #{"content-key"
                  "targeting-key"
                  "identifier-key"
                  "click-through-key"
                  "metadata-key"
                  "clear-cast-id-key"
                  "template-dimensions-key"
                  "third-party-impression-tracker-key"
                  "third-party-click-tracker-key"
                  "optimizing-key"
                  "youtube-description-key"}))

(spec/def ::raw-classification
  (spec/and (spec/map-of string? ::classification-options)
            #(valid-key-frequency? "identifier-key" %)
            #(valid-key-frequency? "click-through-key" %)
            #(valid-key-frequency? "third-party-click-tracker-key" %)
            #(valid-key-frequency? "clear-cast-id-key" %)
            #(valid-key-frequency? "template-dimensions-key" %)
            #(valid-key-frequency? "optimizing-key" %)
            #(valid-key-frequency? "youtube-description-key" %)))

(spec/def ::value
  (spec/keys :req-un [::raw-classification]))

(def validation-messages
  {"identifier-key" "There should be exactly 1 Identifier"
   "click-through-key" "There should be at most 1 Click through"
   "third-party-click-tracker-key" "There should be at most 1 Click tracker"
   "clear-cast-id-key" "There should be at most 1 ClearCast Id"
   "template-dimensions-key" "There should be at most 1 Template Resolution"
   "optimizing-key" "There should be at most 4 Optimizing keys"
   "youtube-description-key" "There should be exactly 1 YouTube description"})

(p/defphraser #(valid-key-frequency? key-type %)
  [_ {:keys [val] :as problem} key-type]
  #_:clj-kondo/ignore
  (let [idxs (->> val
                  (keep (fn [[idx k]]
                          (when (= k key-type)
                            idx))))]
    {:message (get validation-messages key-type)
     :ins (or (seq (map #(conj (:in problem) %) idxs))
              (list (conj (:in problem) (keyword key-type))))
     :problem problem}))

(def validate-form
  (rf/enrich
   (form/get-validate-form-fn :form.content-variant.column-classification ::value)))

(rf/reg-event-fx :form.content-variant.column-classification/open
  [(rf/inject-cofx :sub :resource.feed-importers/one)]
  (fn [{:resource.feed-importers/keys [one]} _]
    (let [feed-id (get-in one [:value :id])]
      {:fx [[:async-flow (field-classes-flow feed-id)]
            [:dispatch [:form.content-variant.column-classification/init]]]})))

(def placeholder-type->column-classification
  {"text" "content-key"
   "image" "content-key"
   "video" "content-key"
   "audio" "content-key"
   "click" "click-through-key"})

(rf/reg-event-fx :form.content-variant.column-classification/auto-map-fields
  [(rf/inject-cofx :sub :form.content-variant/fields)
   (rf/inject-cofx :sub :resource.template/placeholder-name->placeholder-type)]
  (fn [{:keys [db resource.template/placeholder-name->placeholder-type] :as cofx} _]
    (let [;; The fields in the feed
          field-names (get-in cofx [:form.content-variant/fields :value])
          ;; Their relation to column-classification, when their name matches the placeholder.
          field-name->column-classification (->> field-names
                                                 (keep (fn [field-name]
                                                         (when-some [placeholder-type (-> field-name placeholder-name->placeholder-type)]
                                                           [field-name (-> placeholder-type placeholder-type->column-classification)]))))]
      {:db (update-in db [:form.content-variant.column-classification :value :raw-classification]
                      into field-name->column-classification)})))

(rf/reg-sub :form.content-variant.column-classification/auto-map-disabled
  :<- [:resource.template/placeholder-name->placeholder-type]
  (fn [placeholder-name->placeholder-type _]
    (empty? placeholder-name->placeholder-type)))

(rf/reg-event-fx :form.content-variant/update-field-classes
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)
   validate-form]
  (fn [{:keys [db resource.user/token url-context]} [_ payload next-tab]]
    (let [feed-id (get-in db [:resource.adset/one :value :feedId])
          mapping-payload (select-keys (get-in db [:form.content-variant.sheet-import :value]) [:id :mappings])
          feed-fields (->> mapping-payload :mappings (mapv :to))
          ;; Remove any columns that were previously classified, but are no longer present in the feed.
          classification-payload (-> (:raw-classification payload)
                                     (select-keys feed-fields)
                                     (->external-classification))]
      (-> {:db db}
          (loader.db/open-loader)
          (mr/conj-fx [:async-flow {:id :form.content-variant/update-field-classes-flow
                                    :first-dispatch [:resource.feed-importers/update-field-classes feed-id classification-payload]
                                    :rules [{:when :seen?
                                             :events :resource.feed-importers/update-field-classes-success
                                             :halt? false
                                             :dispatch [:form.content-variant/update-field-classes-success next-tab]}
                                            {:when :seen?
                                             :events :resource.feed-importers/get-content-feed-success
                                             :halt? true
                                             :dispatch [:form.content-variant/get-content-feed-success]}
                                            {:when :seen?
                                             :events :resource.feed-importers/update-field-classes-failure
                                             :halt? true
                                             :dispatch-fn (fn [[event response]]
                                                            [[:form.content-variant/update-field-classes-failure event response]])}]}])
          (feed-imports/http-patch-feed url-context mapping-payload token {:on-success [:form.content-variant/update-mapping-on-success]
                                                                           #_#_:on-failure [:form.content-variant/update-mapping-on-failure]})))))

(rf/reg-event-fx :form.content-variant/update-mapping-on-success
  (fn [{:keys [db]} [_ response]]
    (-> {:db db}
        (feed-imports/on-feed-importers-one-success response))))

#_(rf/reg-event-fx :form.content-variant/update-mapping-on-failure
   (fn [{:keys [db]} [_ response]]))

(rf/reg-event-fx :form.content-variant/update-field-classes-success
  (fn [{:keys [db]} [_ next-tab]]
    (-> {:db db}
        (loader.db/close-loader)
        (mr/conj-fx [:dispatch [:response-handler/handle-response
                                {:entity-name entity-name
                                 :type :success
                                 :message (get-in ui-messages [:edit :success])}]])
        (mr/conj-fx-using-db (fn [db]
                               (let [feed-id (get-in db [:resource.adset/one :value :feedId])]
                                 [:dispatch [:resource.feed-importers/get-content-feed feed-id]])))
        (cond-> (nil? (next-tab))
          (mr/conj-fx [:dispatch [:ocp/close]])))))

(rf/reg-event-fx :form.content-variant/update-field-classes-failure
  (fn [{:keys [db]} [_ event response]]
    (-> {:db db}
        (loader.db/close-loader)
        (mr/conj-fx [:dispatch [:response-handler/handle-response
                                {:entity-name entity-name
                                 :type :error
                                 :event event
                                 :message (or
                                           (get-in response [:value :error :message])
                                           (get-in ui-messages [:edit :failure]))
                                 :response response}]]))))

(rf/reg-sub :form.content-variant/fields
  :<- [:resource.feed-importers/fields]
  (fn [fields]
    (update fields :value #(mapv util/sanitize-fieldname %))))

(def use-error-message (partial form/use-error-message :form.content-variant.column-classification))

(def use-bind-control (partial form/use-bind-control :form.content-variant.column-classification))

(form/register-form {:namespace :form.content-variant.column-classification
                     :validate-form validate-form
                     :submit-event :form.content-variant/update-field-classes})
